/**
 * Load workflow + department assessor task + market options for Annual Assessment form.
 * Flow: list workflows → get workflow → find Department/Impact Assessment task → get task detail + Country (markets) + Department (resolve name).
 */

import { useState, useEffect, useCallback } from "react";
import {
  listRegulationWorkflows,
  getWorkflow,
  getWorkflowTask,
  getConceptValues,
  type WorkflowDetail,
  type WorkflowTaskDetail,
  type ProcessTaskInstance,
  type ConceptValue,
} from "../services/regulationsService";
import { getUserId } from "../services/apiClient";

const TRIAGE_TASK_NAME = "Assessment Triage";

/** Task name patterns for department assessor (first match wins) */
const DEPT_ASSESSOR_TASK_PATTERNS = [
  "Department Assessment",
  "Impact Assessment",
  "Department Assessor",
];

function getFieldValue(
  taskDetail: WorkflowTaskDetail | null,
  field: string
): string | undefined {
  const list = taskDetail?.task?.propertyValueItemList;
  if (!Array.isArray(list)) return undefined;
  const item = list.find((p) => p.field === field);
  const v = item?.fieldValue;
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

function findDepartmentAssessorTask(
  instances: ProcessTaskInstance[]
): ProcessTaskInstance | null {
  if (!Array.isArray(instances) || instances.length === 0) return null;
  const byName = instances.find((t) => {
    const name = String(t.taskInstanceName ?? "").toLowerCase();
    if (name === TRIAGE_TASK_NAME.toLowerCase()) return false;
    return DEPT_ASSESSOR_TASK_PATTERNS.some(
      (p) => name.includes(p.toLowerCase())
    );
  });
  if (byName) return byName;
  const notTriage = instances.find(
    (t) =>
      String(t.taskInstanceName ?? "").toLowerCase() !==
      TRIAGE_TASK_NAME.toLowerCase()
  );
  return notTriage ?? instances[0] ?? null;
}

function formatAssessmentDate(value: string | undefined): string {
  if (value?.trim()) return value.trim();
  const d = new Date();
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day.toString().padStart(2, "0")}-${month}-${year}`;
}

export interface DepartmentAssessorTaskData {
  workflowId: string | null;
  taskId: string | null;
  taskDefinitionId: string | null;
  taskInstanceUniqueId: string | null;
  processAssociationId: string | null;
  taskDetail: WorkflowTaskDetail | null;
  departmentDisplayName: string;
  assessor: string;
  assessmentDate: string;
  marketOptions: Array<{ code: string; name: string }>;
}

export interface UseDepartmentAssessorTaskDataResult extends DepartmentAssessorTaskData {
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDepartmentAssessorTaskData(
  regulationId: string | undefined,
  regulationVersion: string | undefined
): UseDepartmentAssessorTaskDataResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskDefinitionId, setTaskDefinitionId] = useState<string | null>(null);
  const [taskInstanceUniqueId, setTaskInstanceUniqueId] = useState<string | null>(null);
  const [processAssociationId, setProcessAssociationId] = useState<string | null>(null);
  const [taskDetail, setTaskDetail] = useState<WorkflowTaskDetail | null>(null);
  const [departmentDisplayName, setDepartmentDisplayName] = useState<string>("");
  const [assessor, setAssessor] = useState<string>("");
  const [assessmentDate, setAssessmentDate] = useState<string>("");
  const [marketOptions, setMarketOptions] = useState<
    Array<{ code: string; name: string }>
  >([]);

  const fetch = useCallback(async () => {
    if (!regulationId || !regulationVersion) {
      setLoading(false);
      setError(null);
      setWorkflowId(null);
      setTaskId(null);
      setTaskDetail(null);
      setDepartmentDisplayName("");
      setAssessor(getUserId() || "");
      setAssessmentDate(formatAssessmentDate(undefined));
      setMarketOptions([]);
      return;
    }

    setLoading(true);
    setError(null);
    const versionNumber = regulationVersion.startsWith("v")
      ? regulationVersion.substring(1)
      : regulationVersion;

    try {
      setAssessor(getUserId() || "");

      const listRes = await listRegulationWorkflows(regulationId, versionNumber);
      const workflows = listRes.data ?? [];
      const first = workflows[0];
      const wfId =
        first?.uuid ?? (first as { workflow_id?: string })?.workflow_id ?? (first as { id?: string })?.id;
      if (!wfId) {
        setWorkflowId(null);
        setTaskId(null);
        setTaskDetail(null);
        setDepartmentDisplayName("");
        setAssessmentDate(formatAssessmentDate(undefined));
        setMarketOptions([]);
        setLoading(false);
        return;
      }
      setWorkflowId(String(wfId));

      const [wfRes, countryRes, deptRes] = await Promise.all([
        getWorkflow(String(wfId)),
        getConceptValues("Country"),
        getConceptValues("Department"),
      ]);

      const detail = wfRes.data as WorkflowDetail | null;
      const instances = detail?.processTaskInstances ?? [];
      const task = findDepartmentAssessorTask(instances);
      if (!task) {
        setTaskId(null);
        setTaskDefinitionId(null);
        setTaskInstanceUniqueId(null);
        setProcessAssociationId(null);
        setTaskDetail(null);
        setDepartmentDisplayName("");
        setAssessmentDate(formatAssessmentDate(undefined));
        setMarketOptions(
          (countryRes.data ?? []).map((c: ConceptValue) => ({
            code: c.code ?? c.id ?? "",
            name: c.name ?? "",
          }))
        );
        setLoading(false);
        return;
      }

      const tId =
        task.taskId ?? (task as { taskId?: string }).taskId ?? (task as { id?: string }).id;
      if (!tId) {
        setTaskId(null);
        setTaskDetail(null);
        setMarketOptions(
          (countryRes.data ?? []).map((c: ConceptValue) => ({
            code: c.code ?? c.id ?? "",
            name: c.name ?? "",
          }))
        );
        setLoading(false);
        return;
      }
      setTaskId(String(tId));
      setTaskDefinitionId(
        task.taskDefinitionId != null ? String(task.taskDefinitionId) : null
      );
      setTaskInstanceUniqueId(
        task.taskInstanceUniqueId != null
          ? String(task.taskInstanceUniqueId)
          : null
      );
      setProcessAssociationId(
        task.processAssociationId != null
          ? String(task.processAssociationId)
          : null
      );

      const taskRes = await getWorkflowTask(String(wfId), String(tId));
      const taskData = taskRes.data ?? null;
      setTaskDetail(taskData);

      const deptCode = getFieldValue(taskData, "department");
      const deptList: ConceptValue[] = deptRes.data ?? [];
      const deptName =
        deptCode && deptList.length
          ? deptList.find((c) => (c.code ?? c.id) === deptCode)?.name ?? deptCode
          : deptCode ?? "";
      setDepartmentDisplayName(deptName);

      const dateVal =
        getFieldValue(taskData, "assessment_date") ??
        getFieldValue(taskData, "due_by") ??
        getFieldValue(taskData, "dateOfImpactAssessment");
      setAssessmentDate(formatAssessmentDate(dateVal));

      setMarketOptions(
        (countryRes.data ?? []).map((c: ConceptValue) => ({
          code: c.code ?? c.id ?? "",
          name: c.name ?? "",
        }))
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load department assessor task data"
      );
      setWorkflowId(null);
      setTaskId(null);
      setTaskDefinitionId(null);
      setTaskInstanceUniqueId(null);
      setProcessAssociationId(null);
      setTaskDetail(null);
      setDepartmentDisplayName("");
      setAssessmentDate(formatAssessmentDate(undefined));
      setMarketOptions([]);
    } finally {
      setLoading(false);
    }
  }, [regulationId, regulationVersion]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    loading,
    error,
    workflowId,
    taskId,
    taskDefinitionId,
    taskInstanceUniqueId,
    processAssociationId,
    taskDetail,
    departmentDisplayName,
    assessor,
    assessmentDate,
    marketOptions,
    refetch: () => {
      void fetch();
    },
  };
}
